import { Component, h, Prop, State, Event, EventEmitter, Fragment, forceUpdate } from '@stencil/core';
import { Tag, User } from '../../global/models';
import { modalController } from '@ionic/core';
import { ActivityCodeDoc, TagData } from 'common';
import { first, last } from 'lodash';

@Component({
  tag: 'concourse-tag-selector',
  styleUrl: 'concourse-tag-selector.css',
  //shadow: true,
})
export class ConcourseTagSelector {
  static async presentModal(projectId: string, suggestion: string, callback: (tag: TagData) => void): Promise<void> {
    const modal: any = await modalController.create({
      component: 'concourse-tag-selector',
      componentProps: { mode: 'multiple', projectId, suggestion },
      showBackdrop: true,
      backdropDismiss: false,
    });

    modal.addEventListener('selected', async (e: CustomEvent) => {
      callback(e.detail as TagData);
      modal.dismiss();
    });

    modal.addEventListener('dismiss', () => {
      modal.dismiss();
    });

    modal.present();
  }

  @Prop() projectId: string;
  @Prop() suggestion: string;

  @State() searchTerm: string = '';

  @Event() dismiss: EventEmitter;
  @Event() selected: EventEmitter<TagData>;

  private activityCodes: ActivityCodeDoc[] = [];

  get user(): User {
    return globalThis.user;
  }

  async componentWillLoad() {
    // This error should never happen, these roles are assigned to everyone
    if (!this.user.hasOneOfRoles([`p-${this.projectId}`, `p-${this.projectId}-basic`])) {
      console.error(`User needs one of p-${this.projectId} or p-${this.projectId}-basic to access tags`);
    }

    this.activityCodes = await Tag.getActivityCodes(this.projectId, this.onActivityCodesComplete.bind(this));
    this.searchTerm = this.suggestion || '';
  }

  async onActivityCodesComplete(docs: ActivityCodeDoc[]) {
    this.activityCodes = docs;
  }

  async onSearchKeyPress(event: KeyboardEvent, searchResults: ActivityCodeDoc[], createTags: string[] = [], lastTag?: TagData) {
    console.log('onSearchKeyPress', event);
    if (event.key == 'Tab') {
      if (searchResults.length > 0) {
        const group = searchResults[searchResults.length - 1];
        if (group.values && group.values.length) {
          const lastTag = group.values[group.values.length - 1];
          const ancestors = Tag.getTagAncestors(lastTag, group.values);
          if (ancestors.length == 0) {
            this.searchTerm = `${group.name}/${lastTag.name}`;
          } else {
            this.searchTerm = `${group.name}/${ancestors.map(tag => tag.name).join('/')}/${lastTag.name}`;
          }

          // if the tag has chilren, we append a / so that the next level of suggestions will be presented
          const groupUnfiltered = this.activityCodes.find(a => a.unique_id == group.unique_id);
          const lastTagChildren = groupUnfiltered.values.filter(t => t.parent_unique_id == lastTag.unique_id);
          if (lastTagChildren.length) {
            this.searchTerm = this.searchTerm + '/';
          }
        } else {
          this.searchTerm = group.name + '/';
        }
      }
      (event.currentTarget as HTMLIonSearchbarElement).setFocus();
    }

    if (event.key == 'Enter' && this.searchTerm.includes('/')) {
      this.createActivityCodeAndTags(createTags, lastTag);
    }
    if (event.key == 'Enter' && !this.searchTerm.includes('/')) {
      this.showGroupSelector();
    }
  }

  async showGroupSelector() {
    const modal: any = await modalController.create({
      component: 'activity-code-selector',
      componentProps: { activityCodes: this.activityCodes, projectId: this.projectId },
      showBackdrop: true,
    });

    modal.addEventListener('selected', async (e: CustomEvent) => {
      // hide the group modal
      modal.dismiss();

      const maxUniqueId = await Tag.getMaxUniqueIdFromTags(this.projectId);
      const tag: TagData = { unique_id: maxUniqueId + 1, name: this.searchTerm, desription: '' };

      // First store the tag on the group
      const activityCode: ActivityCodeDoc = e.detail as ActivityCodeDoc;
      activityCode.values = activityCode.values || [];
      activityCode.values.push(tag);
      Tag.saveActivityCode(this.projectId, activityCode);

      // Then emit the selected tag
      this.searchTerm = '';
      this.selected.emit(tag);
    });

    modal.addEventListener('dismiss', () => {
      modal.dismiss();
    });

    modal.present();
  }

  async createActivityCodeAndTags(values: string[], lastTag?: TagData) {
    const parts = this.searchTerm.split('/').filter(p => p.length > 0);
    // This means that there were no matches, and we create the entire chain starting with activity code.
    if (parts.length == values.length) {
      // iterate and skip the first value, because is the name of activity code
      this.createActivityCode(values);
    } else {
      // This means that we found a match, and we only need to create the missing
      this.createTags(parts[0], values, lastTag);
    }
  }

  async createActivityCode(values: string[]): Promise<void> {
    const maxUnique_id = await Tag.getMaxUniqueIdFromTags(this.projectId);
    let parent_unique_id = null;
    var tags: TagData[] = [];
    for (var i = 1; i < values.length; i++) {
      const unique_id = maxUnique_id + i;
      tags.push({ unique_id, parent_unique_id, name: values[i], color: '#FFA500' });
      parent_unique_id = unique_id;
    }
    const max_unique_id = await Tag.getMaxUniqueIdFromActivityCodes(this.projectId);
    const activityCode: ActivityCodeDoc = { unique_id: max_unique_id + 1, name: values[0], values: tags };
    this.activityCodes = await Tag.saveActivityCode(this.projectId, activityCode);
    if (tags.length > 0) {
      this.selectTag(last(tags)); // emit the last tag created
    }
  }

  async createTags(activityCodeName: string, tagNames: string[], parent: TagData = null): Promise<void> {
    const maxUnique_id = await Tag.getMaxUniqueIdFromTags(this.projectId);
    const group = Tag.getActivityCode(this.activityCodes, activityCodeName);

    var parent_unique_id = parent ? parent.unique_id : null;
    for (var i = 0; i < tagNames.length; i++) {
      const unique_id = maxUnique_id + 1 + i;
      group.values.push({ unique_id, parent_unique_id, name: tagNames[i], color: '#FFA500' });
      parent_unique_id = unique_id;
    }

    // update the activity code in bd in the last index of values.
    Tag.saveActivityCode(this.projectId, group);
    if (group.values.length > 0) {
      this.selectTag(last(group.values)); // emit the last tag created
    }
  }

  onSearchIonInput(searchTerm) {
    this.searchTerm = searchTerm;
  }

  selectTag(tag: TagData) {
    this.selected.emit(tag);
  }

  /**
   * Gets the exact match of parent/child tags within a group.
   * @param group The group within which we try to find the exact matches iteratively
   * @param parts The strings that are used successively to match the tags.
   * @returns A list of TagData that was found using exact match.
   */
  getMatchingTags(group: ActivityCodeDoc, parts: string[]): TagData[] {
    // the remaining terms except the last must be an exact match of a tag in the children
    let children = Tag.getRootTags(group);
    let matches: TagData[] = [];
    for (let i = 0; i < parts.length; i++) {
      const tag = children.find(t => t.name.toLowerCase() == parts[i]);
      if (tag) {
        matches.push(tag);
        children = group.values.filter(t => t.parent_unique_id == tag.unique_id);
      }
    }
    return matches;
  }

  /**
   * When a searchterm ends with "/", then we show all downstream tags as suggestions.
   * This gives the user a nicer experience as they can see all the descendents of the last tag/group
   * @param group The group within which we must look for suggestions.
   * @param tag The optional last tag if we want to only suggest tags under this.
   * @returns a list of TagData.
   */
  getSuggestions(group: ActivityCodeDoc, tag: TagData): TagData[] {
    // If the search results now includes a tag, we get the descendents of the last tag.
    // Otherwise we simply pass all the original values for that
    return tag ? Tag.getTagDecendents(tag, group.values) : group.values;
  }

  // selectMethod is called when the user presses 'Enter' or clicks on the 'CREATE TAG' or 'CREATE ACTIVITY' or 'CREATE PATH'.
  selectMethod(createTags: string[], lastTag?: TagData) {
    if (createTags.length > 0) {
      if (createTags.length == 1 && !this.searchTerm.includes('/')) {
        this.showGroupSelector();
      } else {
        this.createActivityCodeAndTags(createTags, lastTag);
      }
    }
  }

  /**
   * // text for show in the footer button
   */
  get buttonLabel() {
    if (this.searchTerm.includes('/') && this.searchTerm.split('/')[1].length > 0) return 'CREATE PATH';

    if (this.searchTerm.includes('/')) return 'CREATE ACTIVITY';

    return 'CREATE TAG';
  }

  render() {
    let searchResults = this.activityCodes;

    // createTags are the tags that must be created to fulfil the searchTerm.
    let createTags = [];
    //const tagLast: TagData; // here push the last tag that was found
    if (this.searchTerm.indexOf('/') > 0) {
      // we take the seachTerm 'new/uncle1/ta and break that into 'new' 'uncle' and 'ta'
      const parts = this.searchTerm
        .toLowerCase()
        .split('/')
        .filter(p => p.length > 0);
      const searchFragment = last(parts);
      const group = Tag.getActivityCode(this.activityCodes, first(parts));
      if (group) {
        const tags = this.getMatchingTags(group, parts.slice(1));
        const lastTag = last(tags);
        if (this.searchTerm.endsWith('/') && parts.length == tags.length + 1) {
          const suggestions = lastTag ? Tag.getTagDecendents(lastTag, group.values) : group.values;
          tags.push(...suggestions);
        } else if (parts.length > tags.length + 1) {
          // we have a hanging search term at the end of the parts
          const children = lastTag ? group.values.filter(t => t.parent_unique_id == lastTag.unique_id) : Tag.getRootTags(group);
          const childrenFiltered = searchFragment ? Tag.filterTags(children, searchFragment) : children;

          createTags.push(...parts.slice(tags.length + 1));
          tags.push(...childrenFiltered);
        } else if (lastTag){
          // we had an exact match of the last tag, so we search the siblings of the last tag incase there is overlap
          const siblings = group.values.filter(t => t.parent_unique_id == lastTag.parent_unique_id && t.unique_id != lastTag.unique_id);
          const siblingsFiltered = Tag.filterTags(siblings, searchFragment)
          tags.push(...siblingsFiltered);
        }
        searchResults = [{ ...group, values: tags }];
      } else {
        // We are creating everything from scratch including the activity code (group).
        searchResults = [];
        createTags = parts;
      }
    } else {
      searchResults = Tag.filterTagsInActivityCodes(this.activityCodes, this.searchTerm.replace('#', ''));
      if (searchResults.length == 0) {
        createTags.push(this.searchTerm);
      }
    }
    // this.enterTags=createTags
    return (
      <Fragment>
        <ion-header>
          <ion-toolbar>
            <ion-searchbar
              slot="secondary"
              placeholder="Search Tags"
              value={this.searchTerm}
              onKeyUp={(e: KeyboardEvent) => this.onSearchKeyPress(e, searchResults, createTags, last(searchResults))}
              onIonInput={(e: CustomEvent) => {
                this.onSearchIonInput(e.detail.value);
              }}
            ></ion-searchbar>
            <ion-button fill="clear" slot="primary" onclick={() => this.dismiss.emit()}>
              <ion-icon name="close" />
            </ion-button>
          </ion-toolbar>
        </ion-header>
        <ion-content class="ion-padding">
          <div class="search-results">
            {this.activityCodes.length == 0 && this.searchTerm.length == 0 && (
              <div class="result-section-title">There are no activities or tags in the project. Type to create a new tag name </div>
            )}
            {this.activityCodes.length == 0 && this.searchTerm.length > 0 && (
              <div class="result-section-title">
                There are no activities or tags in the project. Press 'Enter' or <a onClick={() => this.createActivityCodeAndTags(createTags)}>click here</a> to create a new tag
                named '{this.searchTerm}'
              </div>
            )}
            {this.activityCodes.length > 0 && searchResults.length == 0 && !this.searchTerm.includes('/') && (
              <div class="result-section-title">
                No tags found. Press 'Enter' or <a onClick={() => this.showGroupSelector()}>click here</a> to create a new tag named '{this.searchTerm}'
              </div>
            )}
            {this.activityCodes.length > 0 && searchResults.length == 0 && this.searchTerm.includes('/') && this.searchTerm.split('/')[1] == '' && (
              <div class="result-section-title">
                No Activitys found. Press 'Enter' or <a onClick={() => this.createActivityCodeAndTags(createTags)}>click here</a> to create a new Activity named '
                {this.searchTerm.replace('/', '')}'
              </div>
            )}
            {this.activityCodes.length > 0 && searchResults.length == 0 && this.searchTerm.includes('/') && this.searchTerm.split('/')[1].length > 1 && (
              <div class="result-section-title">
                No path found. Press 'Enter' or <a onClick={() => this.createActivityCodeAndTags(createTags)}>click here</a> to create a new path '{this.searchTerm}'
              </div>
            )}
            {searchResults.map(group => {
              // Find the values in the root that have no parent id and return their unique ids
              const rootTags = Tag.getRootTags(group);
              const groupName = this.searchTerm ? group.name.replace(`/${this.searchTerm}/gi`, `<b>${this.searchTerm}</b>`) : group.name;
              return (
                <div>
                  <div class="tag-group">
                    <span innerHTML={groupName} />
                  </div>
                  <div>
                    {rootTags.map(tag => {
                      return this.renderChildTagsRecursive(group.values, tag.unique_id, 0);
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </ion-content>
        {createTags.length > 0 && (
          <ion-footer>
            <ion-toolbar class="ion-padding-horizontal">
              <ion-button slot="end" onClick={() => this.selectMethod(createTags, last(searchResults))}>
                <ion-label>
                  {this.buttonLabel} {createTags.join('/')}'
                </ion-label>
              </ion-button>
            </ion-toolbar>
          </ion-footer>
        )}
      </Fragment>
    );
  }

  renderChildTagsRecursive(tags: TagData[], parentUniqueId: number, indent: number = 0) {
    console.log(`renderChildTagsRecursive: parentId:${parentUniqueId}, indent:${indent}`);
    if (indent > 5) return; // Failsafe in-case of recursive loop...
    const parent = tags.find(v => v.unique_id == parentUniqueId);
    const children = tags.filter(v => v.parent_unique_id == parentUniqueId);
    const term = this.searchTerm.includes('/') ? this.searchTerm.substring(this.searchTerm.lastIndexOf('/') + 1) : this.searchTerm;
    const tagName = this.searchTerm ? parent.name.replace(new RegExp(term, 'i'), `<b>${term}</b>`) : parent.name;
    return (
      <div>
        <div
          class="tag search-results-tag"
          style={{ marginLeft: `${indent * 20}px` }}
          onClick={() => {
            this.selectTag(parent);
          }}
        >
          #<span innerHTML={tagName} />
        </div>
        {children.map(tag => {
          return this.renderChildTagsRecursive(tags, tag.unique_id, indent + 1);
        })}
      </div>
    );
  }
}
